import { getAccessCode, getFallbackUrl, getRedirectUrl } from "@/lib/utils";
import { ErrorCodeType } from "@/types";
import { useCallback, useEffect, useState } from "react";
import { useFailure } from "@/hooks/useFailure";

export const useGenerateQrCode = () => {
  const [state, setState] = useState<"loading" | "error" | "success">("loading");
  const [errorCode, setErrorCode] = useState<ErrorCodeType | undefined>(undefined);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [token, setToken] = useState("");
  const [availableTime, setAvailableTime] = useState(0);

  const { handleRedirectFailure } = useFailure();

  const initializeQrCode = () => {
    setQrCodeUrl("");
    setToken("");
    setAvailableTime(0);
  };

  const fetch = useCallback(() => {
    setState("loading");

    nest.generateQrCode({
      success(response) {
        setQrCodeUrl(response.qrCodeUrl);
        setToken(response.token);
        setAvailableTime(response.availableTime);
        setState("success");
      },
      failure(error) {
        setErrorCode(error.code);
        setState("error");
        initializeQrCode();
        if (
          error.code === "blocked_international_signin" ||
          error.code === "request_parent_agreement" ||
          error.code === "withdrawal_grace_period" ||
          error.code === "protected_by_nass"
        ) {
          handleRedirectFailure(error, { setErrorCode, accesscode: getAccessCode() });
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(() => {
    nest.signInQr({
      token: token,
      accessCode: getAccessCode(),
      redirectUrl: getRedirectUrl(),
      fallbackUrl: getFallbackUrl(),
      failure: (error) => {
        if (error.code === "qr_auth_waiting") {
          // QR코드 인증 대기 중
          return;
        }

        setState("error");
        setErrorCode(error.code);
        initializeQrCode();
      },
    });
  }, [token]);

  useEffect(() => {
    if (state === "error") {
      return;
    }

    if (availableTime <= 0) {
      setState("error");
      setErrorCode("qr_auth_timeout");
      return;
    }

    if (availableTime !== 180 && availableTime % 3 === 0) {
      signIn();
    }

    const timer = setTimeout(() => {
      setAvailableTime((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [availableTime, signIn, state]);

  return {
    state,
    errorCode,
    qrCodeUrl,
    availableTime,
    fetch,
  };
};
